// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { generateLinksFrom } from "@insite/client-framework/Components/PageBreadcrumbs";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import LinksState from "@insite/client-framework/Store/Links/LinksState";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import { Container } from "@rugby/App/Components/Container/Container";
import RugbyBreadcrumbs from "@rugby/MobiusOverrides/Breadcrumbs";
import { LinkProps } from "@rugby/MobiusOverrides/Link";
import React from "react";
import { connect } from "react-redux";

const enum fields {
    maxNoOfLinks = "maxNoOfLinks",
    fullWidth = "fullWidth",
    fluid = "fluid",
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        breadcrumbLinks: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl: getHomePageUrl(state),
    };
};

interface BreadcrumbProps extends WidgetProps {
    fields: {
        [fields.maxNoOfLinks]: number;
        [fields.fullWidth]: boolean;
        [fields.fluid]: boolean;
    };
}

type Props = BreadcrumbProps & ReturnType<typeof mapStateToProps>;

const getBreadcrumbLinks = (
    breadcrumbLinks: LinkProps[] | undefined,
    linksState: LinksState,
    nodeId: string,
    homePageUrl: string,
) => {
    let links = breadcrumbLinks || generateLinksFrom(linksState, nodeId, homePageUrl);
    const homePageLink = { children: translate("Home"), href: homePageUrl };
    if (links.length > 0 && links[0].href !== homePageLink.href) {
        links = [homePageLink, ...links];
    }
    return links;
};

const Breadcrumb: React.FC<Props> = ({ breadcrumbLinks, linksState, nodeId, homePageUrl, fields }: Props) => {
    const links = getBreadcrumbLinks(breadcrumbLinks, linksState, nodeId, homePageUrl);

    return (
        <Container fullWidth={fields.fullWidth} fluid={fields.fluid} data-test-selector="breadcrumb_widget">
            <GridContainer gap={0}>
                <RugbyBreadcrumbs links={links} maxNoOfLinks={fields.maxNoOfLinks} />
            </GridContainer>
        </Container>
    );
};

export const definition: WidgetDefinition = {
    displayName: "Breadcrumb",
    group: "Xngage Custom" as any,
    fieldDefinitions: [
        {
            displayName: "Max number of links",
            name: fields.maxNoOfLinks,
            editorTemplate: "IntegerField",
            defaultValue: 2,
            min: 2,
            fieldType: "General",
            tooltip: "Max number of links that should be display in the breadcrumb",
            isRequired: false,
        },
        {
            name: fields.fullWidth,
            displayName: "Full Width",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
        {
            name: fields.fluid,
            displayName: "Fluid Container",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            isVisible: item => item.fields.fullWidth,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(Breadcrumb),
    definition,
};

export default widgetModule;
